// ** React Imports
import { useEffect, useState, useContext } from "react";
import { useHistory } from "react-router-dom";

// ** Redux & Store & Actions
import { connect, useSelector } from "react-redux";
import {
	getPhotoInfo,
	getPhotoComments,
	getPhotoLikes,
} from "store/modules/userpanel-photos";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import { toast } from "helpers/toastWrapper";

// ** Custom Components
import LoadingContainer from "components/layout/LoadingContainer";
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import { Icon } from "components/icons";
import TableView from "components/layout/TableView";
import LoginAvatarSmall from "components/userpanel/LoginAvatarSmall";
import Checkbox from "components/layout/Checkbox";
import Pagination from "components/layout/Pagination";
import LinkNative from "components/layout/LinkNative";
import DatePresenter from "components/other/DatePresenter";
import NoContent from "components/layout/NoContent";
import { FakeActionButton } from "components/layout/FakeActionButton";
import CustomUserMediaSkeletor from "../../Comments/components/CustomUserMediaSkeletor";
import { StyledCommentTextWrapper } from "components/user/Interactions/InteractiveComment/styles/CommentTextWrapper.styled";
import { StyledCommentContainer } from "components/user/UserMediaSkeletor";
import MediaPostInEditView from "../components/MediaPostInEditView";
import { ButtonSecondary } from "components/layout/Button/ButtonSecondary";

// ** Utils & Helpers
import convertText from "utils/convertText";
import api from "helpers/ApiClient";
import cdnPrefixer from "helpers/cdnPrefixer";
import pseudoFormat from "utils/pseudoFormat";

// ** Custom Hooks
import useProcess from "hooks/useProcess";
import useSelected from "hooks/useSelected";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// #####################################################

const EditPhoto = ({
	photoId,
	getPhotoInfo,
	getPhotoComments,
	getPhotoLikes,
}) => {
	const { t } = useTranslation(["common", "userpanel", "suspensions"]);

	const { isPhone } = useContext(deviceTypeContext);
	const user = useSelector((state) => state.globalUser);

	const [whileRemoving, setWhileRemoving] = useState(false);
	const [pageLikes, setPageLikes] = useState(1);
	const [pageComments, setPageComments] = useState(1);
	const [visibleContent, setVisibleContent] = useState("");
	const history = useHistory();

	const info = useSelector((state) => state.userpanelPhotos?.editPhoto?.info);
	const comments = useSelector(
		(state) => state.userpanelPhotos?.editPhoto?.comments,
	);
	const likes = useSelector(
		(state) => state.userpanelPhotos?.editPhoto?.likes,
	);

	const { loadingContainerProps, process, ready } = useProcess({
		promise: () =>
			getPhotoInfo(photoId, user.id).then((response) => {
				if (response === undefined) history.push("/userpanel/data");
			}),
	});

	useEffect(() => {
		process();
	}, []);

	const {
		selected,
		setSelected,
		hasSelected,
		isChecked,
		isCheckedAll,
		check,
		checkAll,
		checkAllDisabled,
		reset,
	} = useSelected(
		(() => {
			const deepData = [];
			comments?.comments?.map((comment) => {
				deepData.push(`${comment.id}-mainComment`);

				comment.answer_comments?.comments?.map((answerComment) => {
					deepData.push(`${answerComment.id}-answerComment`);
				});
			});

			return deepData;
		})(),
	);

	const handleRemove = () => {
		setWhileRemoving(true);

		const selectedMainCommentsIds = selected
			.filter((val) => val.includes("-mainComment"))
			.map((val) => val.replace("-mainComment", ""));

		const selectedAnswersCommentsIds = selected
			.filter((val) => val.includes("-answerComment"))
			.map((val) => val.replace("-answerComment", ""));

		const selectedMainCommentsObjects = comments.comments.filter(
			(mainComment) => {
				return selectedMainCommentsIds.includes(
					mainComment.id.toString(),
				);
			},
		);

		const answerCommentsIdsInSelectedMainComment = [];

		selectedMainCommentsObjects.forEach((mainComment) => {
			if (mainComment.answer_comments.length === 0) return;
			mainComment.answer_comments.comments.forEach((answerComment) => {
				answerCommentsIdsInSelectedMainComment.push(answerComment.id);
			});
		});

		const filterAnswerComments = selectedAnswersCommentsIds.filter(
			(id) =>
				!answerCommentsIdsInSelectedMainComment.includes(parseInt(id)),
		);
		if (selectedMainCommentsIds.length > 0) {
			api.delete(
				`/comment/multiple?type=photo&ids=${selectedMainCommentsIds.join(
					",",
				)}`,
			)
				.then(() => {
					setWhileRemoving(false);
					process();
					reset();
				})
				.catch((error) => {
					setWhileRemoving(false);
					if (error?.response?.data?.error) {
						toast.error(`${error.response.data.error}`);
					}
				});
		}
		if (filterAnswerComments.length > 0) {
			api.delete(
				`/comment-answer/multiple?ids=${filterAnswerComments.join(",")}`,
			)
				.then(() => {
					setWhileRemoving(false);
					process();
					reset();
				})
				.catch((error) => {
					setWhileRemoving(false);
					if (error?.response?.data?.error) {
						toast.error(`${error.response.data.error}`);
					}
				});
		}
	};

	const labelText = (counters) => {
		if (isPhone) {
			return counters > 0 ? pseudoFormat(counters) : "";
		} else {
			return counters > 0 ? ` ${pseudoFormat(counters)}` : "";
		}
	};

	const likesColumns = [
		{
			key: "like",
			headerName: t("common:like"),
			rowCellProps: {
				maxWidth: "200px",
			},
			rowComponent: ({ data } = {}) => {
				return (
					<LoginAvatarSmall
						direction={false}
						data={data}
						additional={() => {
							return (
								isPhone && (
									<Text color="grey" small fullLine>
										<DatePresenter date={data.date_add} />
									</Text>
								)
							);
						}}
					/>
				);
			},
		},
		{
			key: "date_add",
			showInMain: isPhone,
			headerName: t("common:date"),
			rowComponent: ({ data }) => <DatePresenter date={data.date_add} />,
		},
	];
	const rowComponentCommentCheckbox = (data) => {
		return (
			<Block mr={4} flex alignCenter>
				<Checkbox
					checked={isChecked(`${data.id}-mainComment`)}
					onChange={() => {
						const select = [];
						if (isChecked(`${data.id}-mainComment`)) {
							if (Array.isArray(data?.answer_comments.comments)) {
								data.answer_comments.comments.forEach(
									(answerComment) => {
										if (
											selected.includes(
												`${answerComment.id}-answerComment`,
											)
										) {
											select.push(
												`${answerComment.id}-answerComment`,
											);
										}
									},
								);
							}
							const withoutAnswersAndMain = selected.filter(
								(item) =>
									!select.includes(item) &&
									item !== `${data.id}-mainComment`,
							);

							setSelected(withoutAnswersAndMain);
						} else {
							if (Array.isArray(data?.answer_comments.comments)) {
								data.answer_comments.comments.forEach(
									(answerComment) => {
										if (
											selected.includes(
												`${answerComment.id}-answerComment`,
											)
										) {
											return;
										}

										select.push(
											`${answerComment.id}-answerComment`,
										);
									},
								);
							}

							select.push(`${data.id}-mainComment`);
							setSelected((prev) => [...prev, ...select]);
						}
					}}
					label={`Select row: ${data.login} `}
					withActiveBackgroundColor
					rowKey={data.rowKey}
				/>
			</Block>
		);
	};
	const rowComponentComment = (data) => {
		return (
			<StyledCommentContainer>
				<CustomUserMediaSkeletor
					addBgColor
					noMarginBottom
					withBoxShadow
					size="xsmall"
					user={data}
					checkboxComponent={() => {
						return rowComponentCommentCheckbox(data);
					}}
					headerLeft={
						<Block>
							<Block small color="grey">
								<DatePresenter date={data?.date_add} />
								{data?.info?.edited &&
									` (${t("common:edited")})`}
							</Block>

							<StyledCommentTextWrapper isPhone={isPhone}>
								{convertText({
									pieces: data?.comment,
								})}
							</StyledCommentTextWrapper>
						</Block>
					}
					content={
						<Block flex style={{ maxWidth: "99%" }}>
							<FakeActionButton
								isHeart
								label={
									data?.info?.likes > 0
										? pseudoFormat(data?.info?.likes)
										: null
								}
								labelSize="0.8rem"
								customSvgSize={"15px"}
								customMargin={`0 0 0 0`}
							/>
							<FakeActionButton
								isComment
								labelSize="0.8rem"
								label={labelText(data?.info?.comments)}
								customSvgSize={"15px"}
							/>
						</Block>
					}
				/>
			</StyledCommentContainer>
		);
	};

	const rowComponentCommentAnswer = (data) => {
		return (
			<Block flex style={{ marginLeft: "45px" }} mt={3}>
				<CustomUserMediaSkeletor
					addBgColor
					noMarginBottom
					withBoxShadow
					size="xsmall"
					user={data}
					headerLeft={
						<Block>
							<Block small color="grey">
								<DatePresenter date={data?.date_add} />
								{data?.info?.edited &&
									` (${t("common:edited")})`}
							</Block>

							<StyledCommentTextWrapper isPhone={isPhone}>
								{convertText({
									pieces: data?.comment,
								})}
							</StyledCommentTextWrapper>
						</Block>
					}
					checkboxComponent={() => {
						return (
							<Block mr={4} flex alignCenter>
								<TableView.CheckboxSelectRow
									checked={isChecked(
										`${data.id}-answerComment`,
									)}
									onChange={() => {
										if (
											selected.includes(data.parentName)
										) {
											return;
										}
										check(`${data.id}-answerComment`);
									}}
									label={data.login}
									disabled={selected.includes(
										data.parentName,
									)}
								/>
							</Block>
						);
					}}
					content={
						<Block flex style={{ maxWidth: "99%" }}>
							<FakeActionButton
								isHeart
								labelSize="0.8rem"
								label={
									data?.info?.likes > 0
										? pseudoFormat(data?.info?.likes)
										: null
								}
								customSvgSize={"15px"}
								customMargin={`0 0 0 0`}
							/>
						</Block>
					}
				/>
			</Block>
		);
	};

	const commentsColumns = [
		{
			key: "comment",
			headerComponent: () => (
				<Block>
					<Checkbox
						checked={isCheckedAll}
						onChange={() => checkAll()}
						label="Select all"
						disabled={checkAllDisabled}
						withActiveBackgroundColor
					/>
					<Text ml={4}>{t("common:comment")}</Text>
				</Block>
			),
			rowCellProps: {
				maxWidth: "200px",
			},
			rowComponent: ({ data }) => {
				return (
					<Block flex column>
						{rowComponentComment(data)}
						{data?.answer_comments?.comments &&
							data.answer_comments.comments.map(
								(answerComment) => {
									return rowComponentCommentAnswer({
										...answerComment,
										parentName: `${data.id}-mainComment`,
									});
								},
							)}
					</Block>
				);
			},
		},
	];

	// #####################################################

	return (
		<Block>
			<LoadingContainer
				{...loadingContainerProps}
				loading={comments.loading || likes.loading}
				ready={ready}
				loaderDefault
			>
				<MediaPostInEditView
					type="photo"
					info={info}
					id={photoId}
					process={process}
				/>

				<Block>
					<LinkNative
						href={cdnPrefixer(info.image)}
						key="downloadVideoLink"
						color="primary"
						target="_blank"
						download="img.jpg"
						underline
					>
						<Icon name="arrow-alt-to-bottom" height="1em" />
						<Text ml={1}>{t("userpanel:photos.download")}</Text>
					</LinkNative>
				</Block>
				{info.accepted && (
					<>
						<Block mt={4} flex>
							<ButtonSecondary
								label={pseudoFormat(info.count_like)}
								mr={2}
								icon
								iconNew={
									<Icon name="heart" variant={"regular"} />
								}
								onClick={() => {
									setVisibleContent(
										visibleContent === "likes"
											? ""
											: "likes",
									);
								}}
								active={visibleContent === "likes"}
							/>

							<ButtonSecondary
								label={pseudoFormat(info.count_comment)}
								icon
								iconNew={
									<Icon name="comment" variant={"regular"} />
								}
								onClick={() => {
									setVisibleContent(
										visibleContent === "comments"
											? ""
											: "comments",
									);
								}}
								active={visibleContent === "comments"}
							/>
						</Block>
						{visibleContent === "likes" ? (
							Number(info.count_like) > 0 ? (
								<Block mt={4}>
									<TableView
										data={likes.likes}
										columns={likesColumns}
									/>
									{likes.pagination?.totalpages > 1 && (
										<Block mt={4}>
											<Pagination
												total={
													likes.pagination.totalpages
												}
												current={pageLikes}
												onPage={(page) => {
													getPhotoLikes(
														user.id,
														photoId,
														page,
													);
													reset();
													setPageLikes(page);
												}}
												noTo
											/>
										</Block>
									)}
								</Block>
							) : (
								<NoContent
									title={t("userpanel:likes.receiveEmpty")}
								/>
							)
						) : (
							visibleContent === "comments" &&
							(info.count_comment ? (
								<Block mt={4}>
									<TableView
										data={comments.comments}
										columns={commentsColumns}
										actions={{
											items: [
												{
													onClick: handleRemove,
													icon: "trash",
													label: t(
														"userpanel:photos.delete",
													),
													tooltipInfo: "",
												},
											],
											disabled:
												!hasSelected || whileRemoving,
										}}
									/>
									{comments.pagination?.totalpages > 1 && (
										<Block mt={4}>
											<Pagination
												total={
													comments.pagination
														.totalpages
												}
												current={pageComments}
												onPage={(page) => {
													getPhotoComments(
														user.id,
														photoId,
														page,
													);
													reset();
													setPageComments(page);
												}}
												noTo
											/>
										</Block>
									)}
								</Block>
							) : (
								<NoContent
									title={t("userpanel:comments.receiveEmpty")}
								/>
							))
						)}
					</>
				)}
			</LoadingContainer>
		</Block>
	);
};

// #####################################################

export default connect(null, {
	getPhotoInfo,
	getPhotoComments,
	getPhotoLikes,
})(EditPhoto);

// #####################################################
